import { type ReactNode, createContext, useCallback, useContext, useMemo } from 'react';
import { formatShortcut as formatCoreShortcut, shortcutKeys as coreShortcutKeys } from '@dataroom/core/i18n';
import { layersDe, layersEn } from './layers';
import type { DesignMessageKey } from './design';
import type { PrimitiveMessageKey } from './primitives';

/**
 * i18n of `@dataroom/ui`: the kit never hard-codes a user-facing string. The app passes the member's
 * language to `<UiI18nProvider locale>` (and may override messages); components read them with
 * `useUiT()`. Without a provider the last locale set (default `en`) is used, so toasts mounted
 * outside the router are localized too.
 *
 * The primitives' messages (ui-primitives) are typed here but registered by the component that uses
 * them when its module loads (`registerUiMessages`), so a screen only ships the strings of the
 * primitives it renders (the first paint of `/` stays within its budget).
 */
export type UiMessageKey = keyof typeof layersEn | PrimitiveMessageKey | DesignMessageKey;
export type UiMessages = Record<UiMessageKey, string>;
export type UiLocale = 'en' | 'de' | (string & {});

const builtIn: Record<string, Partial<UiMessages>> = {
  en: { ...layersEn },
  de: { ...layersDe },
};

let currentLocale: UiLocale = 'en';
let currentOverrides: Partial<UiMessages> | undefined;

function lookup(locale: UiLocale, overrides: Partial<UiMessages> | undefined, key: UiMessageKey): string {
  return (
    overrides?.[key] ??
    builtIn[locale]?.[key] ??
    builtIn[locale.split('-')[0] ?? 'en']?.[key] ??
    builtIn.en?.[key] ??
    key
  );
}

export type UiVars = Record<string, string | number>;
export type UiTranslate = (key: UiMessageKey, vars?: UiVars) => string;

function format(
  locale: UiLocale,
  overrides: Partial<UiMessages> | undefined,
  key: UiMessageKey,
  vars?: UiVars,
): string {
  let k = key;
  if (vars && typeof vars.count === 'number') {
    const rule = new Intl.PluralRules(locale).select(vars.count);
    const pluralKey = `${key.replace(/_(one|other)$/, '')}_${rule === 'one' ? 'one' : 'other'}`;
    if (pluralKey in builtIn.en!) k = pluralKey as UiMessageKey;
  }
  const text = lookup(locale, overrides, k);
  return vars
    ? text.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m))
    : text;
}

/** Translate outside React (toast managers, imperative helpers). */
export const uiT: UiTranslate = (key, vars) => format(currentLocale, currentOverrides, key, vars);

export function setUiLocale(locale: UiLocale, overrides?: Partial<UiMessages>): void {
  currentLocale = locale;
  currentOverrides = overrides;
}

export function getUiLocale(): UiLocale {
  return currentLocale;
}

/** Add or replace a locale's messages (a third language, tests). */
export function registerUiMessages(locale: UiLocale, messages: Partial<UiMessages>): void {
  builtIn[locale] = { ...(builtIn[locale] ?? builtIn.en), ...messages };
}

const Ctx = createContext<{ locale: UiLocale; overrides?: Partial<UiMessages> } | null>(null);

export function UiI18nProvider({
  locale,
  messages,
  children,
}: {
  locale: UiLocale;
  /** Per-key overrides for this locale. */
  messages?: Partial<UiMessages>;
  children: ReactNode;
}) {
  // Imperative helpers (toasts, confirm) follow the provider.
  setUiLocale(locale, messages);
  const value = useMemo(() => ({ locale, overrides: messages }), [locale, messages]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUiLocale(): UiLocale {
  return useContext(Ctx)?.locale ?? currentLocale;
}

export function useUiT(): UiTranslate {
  const ctx = useContext(Ctx);
  const locale = ctx?.locale ?? currentLocale;
  const overrides = ctx ? ctx.overrides : currentOverrides;
  return useCallback((key, vars) => format(locale, overrides, key, vars), [locale, overrides]);
}

/** Whether shortcuts are shown with macOS glyphs (macOS and iPadOS). */
export function isMacPlatform(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    /mac|iphone|ipad|ipod/i.test(
      (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
        navigator.platform ??
        '',
    )
  );
}

export type ShortcutOptions = {
  /** macOS glyphs (default: detected from the platform). */
  mac?: boolean;
  /** Language of the key names (default: the kit's current language). */
  locale?: UiLocale;
  /** @deprecated Unused: key names follow `locale`. Kept so existing callers compile. */
  t?: UiTranslate;
};

const shortcutLocale = (locale: UiLocale | undefined): 'en' | 'de' =>
  (locale ?? currentLocale).toLowerCase().startsWith('de') ? 'de' : 'en';
const shortcutPlatform = (mac: boolean | undefined): 'mac' | 'other' =>
  (mac ?? isMacPlatform()) ? 'mac' : 'other';

/**
 * Platform- and language-aware keyboard labels. This is the one formatter of the product
 * (`@dataroom/core`'s, which the app's `formatShortcut` and the shortcut sheet use too):
 * `formatShortcut('mod+shift+k')` gives "⇧⌘K" on macOS and "Ctrl+Shift+K" / "Strg+Umschalt+K"
 * elsewhere; a sequence reads "G then H" / "G dann H".
 */
export function formatShortcut(combo: string, opts: ShortcutOptions = {}): string {
  return formatCoreShortcut(combo, shortcutPlatform(opts.mac), shortcutLocale(opts.locale));
}

/**
 * The keys of a shortcut in display order, `'then'` between the steps of a sequence (key chips, as
 * in the shortcut sheet): `shortcutKeys('g h')` gives `['G', 'then', 'H']`.
 */
export function shortcutKeys(combo: string, opts: ShortcutOptions = {}): string[] {
  return coreShortcutKeys(combo, shortcutPlatform(opts.mac), shortcutLocale(opts.locale));
}

const ARIA_KEYS: Record<string, string> = {
  cmd: 'Meta',
  meta: 'Meta',
  ctrl: 'Control',
  control: 'Control',
  alt: 'Alt',
  option: 'Alt',
  shift: 'Shift',
  enter: 'Enter',
  return: 'Enter',
  esc: 'Escape',
  escape: 'Escape',
  space: 'Space',
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
};

/** A combo in `aria-keyshortcuts` syntax: `mod+shift+k` gives "Meta+Shift+K" on macOS, "Control+Shift+K" elsewhere. */
export function ariaKeyShortcuts(combo: string, opts: { mac?: boolean } = {}): string {
  const mac = opts.mac ?? isMacPlatform();
  const name = (raw: string): string => {
    const key = raw.replace(/^\$/, '').toLowerCase();
    if (key === 'mod') return mac ? 'Meta' : 'Control';
    return (
      ARIA_KEYS[key] ?? (key.length === 1 ? key.toUpperCase() : key.charAt(0).toUpperCase() + key.slice(1))
    );
  };
  return combo
    .trim()
    .split(/\s+/)
    .map((step) => step.split('+').filter(Boolean).map(name).join('+'))
    .join(' ');
}
